import { useEffect } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import { ArrowLeft } from "lucide-react"

import { TournamentLeaderboard } from "@/features/tournament"
import { getUserIdFromToken } from "@/shared/api"
import { useTournamentStore } from "@/shared/store"
import { useI18n } from "@/shared/i18n"
import { useTelegramBackButton } from "@/shared/hooks/useTelegramBackButton"

/**
 * Tournament leaderboard page. The bootstrap-cached `summary` (from the
 * tournament store) is used immediately for the header / redirect guard, and
 * `GET /v1/games/tournaments/{id}` is fired on mount to pull fresh `details`
 * (top-10 + `myStanding` + reward_promises). The UI shows top-10 only.
 */
export default function TournamentLeaderboardPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const useNativeBack = useTelegramBackButton(() => navigate(-1))

  const summary = useTournamentStore((s) => s.summary)
  const details = useTournamentStore((s) => s.details)
  const fetchDetails = useTournamentStore((s) => s.fetchDetails)

  const tournamentId = summary?.id ?? null
  useEffect(() => {
    if (!tournamentId) return
    void fetchDetails(tournamentId)
  }, [tournamentId, fetchDetails])

  // Reached via the Rewards CTA — bounce back if there's no active tournament.
  if (!summary) return <Navigate to="/rewards" replace />

  return (
    <div className="relative min-h-full px-4 pb-10">
      {!useNativeBack && (
        <button
          type="button"
          aria-label={t("tournamentLeaderboard.back")}
          onClick={() => navigate(-1)}
          className="absolute left-0 top-0 z-10 flex size-12 items-center justify-center text-[#F5F5F5] transition-opacity active:opacity-60"
        >
          <ArrowLeft className="size-6" />
        </button>
      )}

      {/* Optimistic render: cover/header from the bootstrap summary (or any
          cached details) immediately, while `fetchDetails` refreshes the full
          payload (top-10 + my_standing + reward_promises) in the background. */}
      <TournamentLeaderboard
        tournament={
          details?.tournament ?? { ...summary, rewardPromises: [] }
        }
        leaderboard={details?.leaderboard ?? []}
        myUserId={getUserIdFromToken()}
      />
    </div>
  )
}
